import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Trash2, Calendar, User, FileText, Filter, Edit, Search, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getCalls, deleteCall, updateCall } from '@/api/api';
import { useActionHistory } from '@/context/ActionHistoryContext';
import * as XLSX from 'xlsx';
import PageHeader from '@/components/PageHeader';

const Calls = ({ user }) => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('call_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState(''); // ARAMA FİLTRESİ
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCall, setEditingCall] = useState(null);
  const [editFormData, setEditFormData] = useState({ call_type: 'Genel', notes: '' });
  const { pushAction } = useActionHistory();
  const itemsPerPage = 100;
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const callsData = await getCalls();
      setCalls(callsData);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (callId) => {
    if (!window.confirm('Bu aramayı silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await deleteCall(callId);
      toast.success('Arama silindi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Arama silinemedi');
      console.error('Error deleting call:', error);
    }
  };

  const handleEdit = (call) => {
    setEditingCall(call);
    setEditFormData({
      call_type: call.call_type,
      notes: call.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!editingCall) return;

    try {
      await updateCall(editingCall.id, {
        customer_id: editingCall.customer_id,
        call_type: editFormData.call_type,
        notes: editFormData.notes
      });
      toast.success('Arama güncellendi');
      setShowEditDialog(false);
      setEditingCall(null);
      loadData();
    } catch (error) {
      toast.error('Arama güncellenemedi');
      console.error('Error updating call:', error);
    }
  };

  const handleExport = () => {
    // Filtre kontrolü - herhangi bir filtre uygulanmış mı?
    const isFiltered = searchQuery.trim() !== '' || filterType !== 'all';
    
    // Filtre varsa filtrelenmiş veriyi, yoksa tüm veriyi kullan
    const dataToExport = isFiltered ? filteredCalls : calls;
    
    const exportData = dataToExport.map(call => ({
      'Müşteri Adı': call.customer_name || '',
      'Temsilci': call.representative || '',
      'Arama Tipi': call.call_type || '',
      'Notlar': call.notes || '',
      'Arama Tarihi': new Date(call.call_date).toLocaleString('tr-TR')
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Aramalar');
    
    const fileName = isFiltered 
      ? `aramalar_filtreli_${exportData.length}_adet.xlsx`
      : `tum_aramalar_${exportData.length}_adet.xlsx`;
    
    XLSX.writeFile(wb, fileName);
    toast.success(`${exportData.length} arama Excel'e aktarıldı${isFiltered ? ' (filtrelenmiş)' : ' (tümü)'}`);
  };


  // Filter calls by type
  const filteredCalls = calls.filter(call => {
    // Tip filtresi
    if (filterType !== 'all' && call.call_type !== filterType) {
      return false;
    }
    
    // Arama filtresi
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        call.customer_name?.toLowerCase().includes(query) ||
        call.representative?.toLowerCase().includes(query) ||
        call.notes?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  useEffect(() => {
    setCurrentPage(1); // Arama veya filtre değişince 1. sayfaya dön
  }, [searchQuery, filterType, sortField, sortOrder]);

  // Calculate statistics by call type
  const getCallStatsByType = () => {
    const stats = {
      'Genel': 0,
      'Abonelik': 0,
      'Portföy': 0,
      'Hoşgeldin': 0
    };
    
    calls.forEach(call => {
      if (stats.hasOwnProperty(call.call_type)) {
        stats[call.call_type]++;
      }
    });
    
    return stats;
  };

  const callStatsByType = getCallStatsByType();

  const sortedCalls = [...filteredCalls].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField === 'call_date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const paginatedCalls = sortedCalls.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedCalls.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="px-2 py-2 space-y-2">
      {/* Header */}
      <PageHeader
        icon={Phone}
        title="Aramalarım"
        subtitle={`Toplam ${filteredCalls.length} arama`}
      >
        <Button onClick={handleExport} className="bg-white text-blue-600 hover:bg-blue-50 shadow-md" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Dışarı Aktar
        </Button>
      </PageHeader>

      {/* Filter - MİNİMUM */}
      <div className="bg-white p-2 rounded shadow space-y-2">
        {/* Arama Kutusu */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Müşteri, temsilci veya not ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-xs font-medium text-gray-700 flex-shrink-0">Tip:</Label>
          <Select value={filterType} onValueChange={(newValue) => {
            const oldValue = filterType;
            setFilterType(newValue);
            pushAction({
              undo: () => setFilterType(oldValue)
            });
          }}>
            <SelectTrigger className="w-full h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="Genel">Genel</SelectItem>
              <SelectItem value="Abonelik">Abonelik</SelectItem>
              <SelectItem value="Portföy">Portföy</SelectItem>
              <SelectItem value="Hoşgeldin">Hoşgeldin</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats - Responsive Grid */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5 md:gap-4">
        <div className="bg-white p-2 md:p-4 rounded-lg shadow min-w-0">
          <div className="text-xs md:text-sm text-gray-600 truncate">Toplam</div>
          <div className="text-lg md:text-2xl font-bold text-blue-600">{calls.length}</div>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg shadow border-l-2 md:border-l-4 border-green-500 min-w-0">
          <div className="text-xs md:text-sm text-gray-600 truncate">Genel</div>
          <div className="text-lg md:text-2xl font-bold text-green-600">{callStatsByType['Genel']}</div>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg shadow border-l-2 md:border-l-4 border-purple-500 min-w-0">
          <div className="text-xs md:text-sm text-gray-600 truncate">Abonelik</div>
          <div className="text-lg md:text-2xl font-bold text-purple-600">{callStatsByType['Abonelik']}</div>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg shadow border-l-2 md:border-l-4 border-orange-500 min-w-0">
          <div className="text-xs md:text-sm text-gray-600 truncate">Portföy</div>
          <div className="text-lg md:text-2xl font-bold text-orange-600">{callStatsByType['Portföy']}</div>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg shadow border-l-2 md:border-l-4 border-pink-500 min-w-0 col-span-2 md:col-span-1">
          <div className="text-xs md:text-sm text-gray-600 truncate">Hoşgeldin</div>
          <div className="text-lg md:text-2xl font-bold text-pink-600 md:text-left text-center">{callStatsByType['Hoşgeldin']}</div>
        </div>
      </div>

      {/* Mobile Card View - ONLY MOBILE */}
      {isMobile && (
        <div className="space-y-1.5 mb-2">
        {paginatedCalls.length === 0 ? (
          <div className="bg-white rounded shadow p-4 text-center text-gray-500 text-xs">
            Henüz arama kaydı yok
          </div>
        ) : (
          paginatedCalls.map((call) => {
            const getCallTypeColor = (type) => {
              switch(type) {
                case 'Genel': return 'bg-green-100 text-green-800 border-green-500';
                case 'Abonelik': return 'bg-purple-100 text-purple-800 border-purple-500';
                case 'Portföy': return 'bg-orange-100 text-orange-800 border-orange-500';
                case 'Hoşgeldin': return 'bg-pink-100 text-pink-800 border-pink-500';
                default: return 'bg-gray-100 text-gray-800 border-gray-500';
              }
            };

            // Düzenleme yetkisi - admin, abonelik, temsilci (kendi kayıtları veya hepsi)
            const canEdit = ['admin', 'abonelik', 'temsilci'].includes(user?.role);
            const colorClass = getCallTypeColor(call.call_type);
            const borderColor = colorClass.split('border-')[1];

            return (
              <div 
                key={call.id} 
                className={`bg-white rounded shadow p-1 border-l-2 border-${borderColor} cursor-pointer hover:bg-gray-50`}
                onClick={() => navigate(`/customers/${call.customer_id}`)}
              >
                <div className="mb-0.5">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-bold text-gray-900 truncate flex-1 mr-1">
                      {call.customer_name}
                    </span>
                    <span className={`px-1 py-0.5 rounded text-xs font-bold ${colorClass.split('border-')[0]} flex-shrink-0`}>
                      {call.call_type}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-0.5 text-xs text-gray-600">
                    <Calendar className="w-2.5 h-2.5 flex-shrink-0" />
                    <span className="truncate">
                      {new Date(call.call_date).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'short'
                      })}
                    </span>
                    {user?.role === 'admin' && (
                      <>
                        <span className="mx-0.5">•</span>
                        <span className="truncate">{call.representative}</span>
                      </>
                    )}
                  </div>
                </div>
                
                {call.notes && (
                  <div className="text-xs text-gray-700 bg-gray-50 px-1 py-0.5 rounded mb-0.5 line-clamp-1 break-words overflow-hidden">
                    {call.notes}
                  </div>
                )}

                {canEdit && (
                  <div className="flex gap-0.5">
                    <Button 
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-6 text-xs px-1 py-0 min-w-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(call);
                      }}
                    >
                      Düzenle
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(call.id);
                      }}
                      className="h-6 px-1.5 py-0 flex-shrink-0"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      )}

      {/* Desktop Table View - ONLY DESKTOP */}
      {!isMobile && (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="progo-gradient">
              <tr>
                <th
                  onClick={() => handleSort('call_date')}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-800 cursor-pointer hover:bg-gray-300"
                >
                  Tarih {sortField === 'call_date' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                {user?.role === 'admin' && (
                  <th
                    onClick={() => handleSort('representative')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-800 cursor-pointer hover:bg-gray-300"
                  >
                    Temsilci {sortField === 'representative' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                )}
                <th
                  onClick={() => handleSort('customer_name')}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-800 cursor-pointer hover:bg-gray-300"
                >
                  Müşteri {sortField === 'customer_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('call_type')}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-800 cursor-pointer hover:bg-gray-300"
                >
                  Arama Tipi {sortField === 'call_type' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-800">
                  Notlar
                </th>
                {/* İşlemler kolonu - admin, abonelik, temsilci için */}
                {['admin', 'abonelik', 'temsilci'].includes(user?.role) && (
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-800">
                    İşlemler
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedCalls.length === 0 ? (
                <tr>
                  <td colSpan={user?.role === 'admin' ? 6 : 5} className="px-6 py-8 text-center text-gray-500">
                    <Phone className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Henüz arama kaydı yok</p>
                    <p className="text-sm mt-1">Müşteri detay sayfasından arama ekleyebilirsiniz</p>
                  </td>
                </tr>
              ) : (
                paginatedCalls.map((call) => {
                  const getCallTypeColor = (type) => {
                    switch(type) {
                      case 'Genel': return 'bg-green-100 text-green-800';
                      case 'Abonelik': return 'bg-purple-100 text-purple-800';
                      case 'Portföy': return 'bg-orange-100 text-orange-800';
                      case 'Hoşgeldin': return 'bg-pink-100 text-pink-800';
                      default: return 'bg-gray-100 text-gray-800';
                    }
                  };

                  // Check if user can edit/delete this call
                  // Düzenleme yetkisi - admin, abonelik, temsilci (kendi kayıtları veya hepsi)
                  const canEdit = ['admin', 'abonelik', 'temsilci'].includes(user?.role);

                  return (
                    <tr 
                      key={call.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/customers/${call.customer_id}`)}
                    >
                      <td className="px-2 py-2 text-xs text-gray-900">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <span className="whitespace-nowrap">
                            {new Date(call.call_date).toLocaleDateString('tr-TR', {
                              day: 'numeric',
                              month: 'short'
                            })}
                          </span>
                        </div>
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-2 py-2">
                          <div className="max-w-[100px] truncate text-xs text-gray-900">
                            {call.representative}
                          </div>
                        </td>
                      )}
                      <td className="px-2 py-2 text-xs font-medium text-gray-900">
                        <div className="max-w-[150px] truncate">
                          {call.customer_name}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getCallTypeColor(call.call_type)}`}>
                          {call.call_type}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-500">
                        <div className="truncate">
                          {call.notes || '-'}
                        </div>
                      </td>
                      {/* Düzenleme/Silme butonları - admin, abonelik, temsilci için */}
                      {['admin', 'abonelik', 'temsilci'].includes(user?.role) && canEdit && (
                        <td className="px-2 py-2 text-center">
                          <div className="flex gap-1 justify-center">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(call);
                                }}
                                className="px-1.5 py-1 h-7"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(call.id);
                                }}
                                className="px-1.5 py-1 h-7"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Desktop Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Toplam <span className="font-medium">{sortedCalls.length}</span> kayıt
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  Önceki
                </Button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Sayfa {currentPage} / {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  Sonraki
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Mobile Pagination - ONLY MOBILE */}
      {isMobile && totalPages > 1 && (
        <div className="flex justify-between items-center gap-2 bg-white p-1.5 rounded shadow">
          <Button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            variant="outline"
            size="sm"
            className="h-7 text-xs px-2"
          >
            ‹
          </Button>
          <span className="text-xs font-medium">
            {currentPage}/{totalPages}
          </span>
          <Button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            variant="outline"
            size="sm"
            className="h-7 text-xs px-2"
          >
            ›
          </Button>
        </div>
      )}

      {/* Edit Call Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-full sm:max-w-[500px] mx-2 sm:mx-4">
          <DialogHeader>
            <DialogTitle>Arama Düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-call-type">Arama Tipi</Label>
              <Select 
                value={editFormData.call_type} 
                onValueChange={(value) => setEditFormData({...editFormData, call_type: value})}
              >
                <SelectTrigger id="edit-call-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Genel">Genel</SelectItem>
                  <SelectItem value="Abonelik">Abonelik</SelectItem>
                  <SelectItem value="Portföy">Portföy</SelectItem>
                  <SelectItem value="Hoşgeldin">Hoşgeldin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notlar</Label>
              <Textarea
                id="edit-notes"
                value={editFormData.notes}
                onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                className="min-h-[100px]"
                placeholder="Arama notları..."
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleUpdate} className="progo-bg hover:bg-blue-700">
              Güncelle
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default Calls;
